import { useState } from "react";

import {
  makeGetApiCall,
  makePostApiCall,
  makePostApiCallToPrinter,
} from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";

export function NewPackageViewModel(logout) {
  const [brands, setBrands] = useState({ data: [] });
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [printingLabelType, setPrintingLabelType] = useState({ type: 2 });
  const [scaleIsOpen, setScaleIsOpen] = useState(false);
  const [printedPackageItems, setPrintedPackageItems] = useState([]);
  const [packagingLabel, setPackagingLabel] = useState({
    brand_image1: null,
    brand_image2: null,
    brand_image3: null,
    brand_name: null,
    address: null,
    lot_number: null,
    product_plu: null,
    ingredients: null,
    product_name: null,
    product_description: null,
    passed_from_metal_detector: null,
    product_weight: null,
    barcodeHtml: null,
    barcode: null,
    printCounter: 0,
  });
  const [printProgress, setPrintProgress] = useState({
    loading: false,
    message: "",
  });
  const [lotNumbers, setLotNumbers] = useState([]);

  const [reprintInProgress, setReprintInProgress] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const getManufacturedItemByLot = async function (lot) {
    const payload = {
      lot: lot,
    };
    let callResult = await makePostApiCall(
      `/manufacturedProducts/lot/${lot}`,
      payload,
    );

    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getManufacturedItemById = async function (productId) {
    const payload = {
      product_id: productId,
    };
    let callResult = await makePostApiCall(
      `manufacturedProducts/id/${productId}`,
      payload,
    );

    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getEmployeesForDropdown = async function (type, department) {
    const payload = { department: department, type: [type] };
    let callResult = await makePostApiCall(`employeesForDropdown`, payload);

    if (callResult.success) {
      if (type === 1) {
        setEmployeeOptions(callResult.data);
      } else {
        setSupervisorOptions(callResult.data);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getInContactPackagingSupplies = async function (page, search) {
    const payload = {
      page: page,
      search: search,
    };
    let callResult = await makePostApiCall(
      `fetchSupplierPackagingItemsByProductType`,
      payload,
    );

    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    return callResult;
  };

  const savePackagingDetailsAndPrint = async function (values) {
    setPrintProgress({ loading: true, message: "" });
    let callResult;
    try {
      callResult = await makePostApiCall("createFromIcap", {
        ...values,
        printer_type: "main",
      });

      if (!callResult || !callResult.success) {
        if (callResult?.errorStatus === 401) {
          logout();
        } else {
          showErrorModal(
            "Error",
            callResult?.data?.subtitle ?? "We could not complete the action.",
          );
        }
        return callResult;
      }

      const labelDetails = callResult.data?.label_details ?? {};
      const cartonLabels = labelDetails.carton_labels ?? [];
      const palletLabel = labelDetails.pallet_label ?? [];
      const labelsToPrint = [...cartonLabels, ...palletLabel];
      const cartonCount = cartonLabels.length;

      for (let i = 0; i < labelsToPrint.length; i++) {
        const element = labelsToPrint[i];
        const isCarton = i < cartonCount;
        if (isCarton) {
          setPrintProgress({
            loading: true,
            message: `${i + 1}/${cartonCount} cartons`,
          });
        } else {
          setPrintProgress({ loading: true, message: "Printing pallet label" });
        }

        const printPayload = {
          ...element,
          net_weight: `${element.net_weight ?? ""}${element.unit_of_measure ?? ""}`,
        };

        const printResult = await makePostApiCallToPrinter(printPayload);

        if (!printResult?.success) {
          const errorMessage =
            printResult?.data?.subtitle ??
            printResult?.data?.message ??
            printResult?.data?.title ??
            "Print failed. Please try again.";
          showErrorModal("Print error", errorMessage);
          callResult = { success: false };
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      if (callResult?.success) {
        setPrintedPackageItems((prev) => [...prev, ...cartonLabels]);
      }
    } catch (err) {
      showErrorModal("Error", "An unexpected error occurred while printing.");
      callResult = { success: false };
    } finally {
      setPrintProgress({ loading: false, message: "" });
    }
    return callResult ?? { success: false };
  };

  const getBrands = async function (search) {
    const payload = {
      page: 1,
      search: search,
    };
    let callResult = await makePostApiCall(`brands`, payload);
    if (callResult.success) {
      setBrands(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const deleteSinglePackagedItem = async function (packageId) {
    setDeleteInProgress(true);
    let callResult = await makePostApiCall(
      `deleteSinglePackagedItem/${packageId}`,
    );
    if (callResult.success) {
      const filteredPackages = printedPackageItems.filter(
        (item) => item.package_id !== packageId,
      );
      setPrintedPackageItems(filteredPackages);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setDeleteInProgress(false);
  };

  const reprintPackageLabel = async function (packageLabelDeatils) {
    setReprintInProgress(true);
    makePostApiCallToPrinter({
      package_id: packageLabelDeatils.package_id,
      brand: packageLabelDeatils.brand,
      address: packageLabelDeatils.address,
      plu: packageLabelDeatils.plu,
      name: packageLabelDeatils.name,
      description: packageLabelDeatils.description,
      ingredients: packageLabelDeatils.ingredients,
      net_weight: `${packageLabelDeatils.net_weight}${packageLabelDeatils.unit_of_measure}`,
      production_date: packageLabelDeatils.production_date,
      best_before_date: packageLabelDeatils.best_before_date,
      passed_from_metal_detector:
        packageLabelDeatils.passed_from_metal_detector,
      lot_number: packageLabelDeatils.lot_number,
      barcode: packageLabelDeatils.barcode,
      logo: packageLabelDeatils.logo,
      cert_1: packageLabelDeatils.cert_1,
      cert_2: packageLabelDeatils.cert_2,
      serial_number: packageLabelDeatils.serial_number,
      unit_of_measure: packageLabelDeatils.unit_of_measure,
    });
    setReprintInProgress(false);
    setPrintProgress({ loading: false, message: "" });
  };

  const fetchProductByPlu = async function (plu) {
    let callResult = await makePostApiCall(`products/plu/${plu}`);
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Product not found");
    }
    return callResult;
  };

  const onSearchLotNumbers = async function (search) {
    let callResult = await makeGetApiCall(`onSearchLotNumbers/${search}`);

    if (callResult.success) {
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      // showErrorModal("Error", "Failed to fetch lot numbers");
      return [];
    }
  };

  return {
    brands,
    emptyModal,
    getBrands,
    getManufacturedItemByLot,
    getManufacturedItemById,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    employeeOptions,
    getEmployeesForDropdown,
    getInContactPackagingSupplies,
    savePackagingDetailsAndPrint,
    showErrorModal,
    printingLabelType,
    scaleIsOpen,
    setScaleIsOpen,
    printedPackageItems,
    deleteSinglePackagedItem,
    reprintPackageLabel,
    packagingLabel,
    setPackagingLabel,
    fetchProductByPlu,
    printProgress,
    reprintInProgress,
    deleteInProgress,
    supervisorOptions,
    lotNumbers,
    onSearchLotNumbers,
  };
}
